import { ethers } from 'ethers';
import { defaultConfig, getConfig } from '../config/bundlerConfig';
import { addRecoverySnapshot } from './recoveryBackup';

const tokenABI = [
    "function approve(address spender, uint256 amount) external returns (bool)",
    "function allowance(address owner, address spender) external view returns (uint256)",
    "function balanceOf(address account) external view returns (uint256)",
    "function transfer(address to, uint256 amount) external returns (bool)",
    "function totalSupply() external view returns (uint256)"
];

const routerABI = [
    "function addLiquidityETH(address token, uint256 amountTokenDesired, uint256 amountTokenMin, uint256 amountETHMin, address to, uint256 deadline) external payable returns (uint256 amountToken, uint256 amountETH, uint256 liquidity)",
    "function swapExactETHForTokens(uint256 amountOutMin, address[] path, address to, uint256 deadline) external payable returns (uint256[] amounts)",
    "function swapExactTokensForETH(uint256 amountIn, uint256 amountOutMin, address[] path, address to, uint256 deadline) external returns (uint256[] amounts)",
    "function swapExactTokensForETHSupportingFeeOnTransferTokens(uint256 amountIn, uint256 amountOutMin, address[] path, address to, uint256 deadline) external",
    "function removeLiquidityETH(address token, uint256 liquidity, uint256 amountTokenMin, uint256 amountETHMin, address to, uint256 deadline) external returns (uint256 amountToken, uint256 amountETH)",
    "function getAmountsOut(uint256 amountIn, address[] path) external view returns (uint256[] memory amounts)",
    "function factory() external view returns (address)",
    "function WETH() external view returns (address)"
];

const factoryABI = [
    "function getPair(address tokenA, address tokenB) external view returns (address pair)"
];

const API_URL = `${defaultConfig.SERVER_URL.url}/api`;

/** Resolve canonical tx hashes from signed serialized txs and wait for receipts. */
async function waitForSignedTransactionsMined(provider, signedRaws, { timeoutMs = 180000 } = {}) {
    const hashes = signedRaws.map((raw, i) => {
        const p = ethers.utils.parseTransaction(raw);
        if (!p?.hash) throw new Error(`Could not derive hash for signed transaction #${i + 1}`);
        return p.hash;
    });
    await Promise.all(
        hashes.map((h) =>
            provider.waitForTransaction(h, 1, timeoutMs).then((rcpt) => {
                if (!rcpt || rcpt.status !== 1) {
                    throw new Error(`Transaction ${h} failed or timed out`);
                }
                return rcpt;
            }),
        ),
    );
}

async function withTimeout(promise, ms, label) {
    let t = null;
    const timeout = new Promise((_, rej) => {
        t = setTimeout(() => rej(new Error(`Timeout${label ? `: ${label}` : ''}`)), ms);
    });
    try {
        return await Promise.race([promise, timeout]);
    } finally {
        if (t) clearTimeout(t);
    }
}

function pickFeeData(feeData, fallbackPriorityGwei = '2') {
    const use1559 = !!feeData?.maxFeePerGas;
    if (use1559) {
        return {
            type: 2,
            maxFeePerGas: feeData.maxFeePerGas,
            maxPriorityFeePerGas: feeData.maxPriorityFeePerGas || ethers.utils.parseUnits(String(fallbackPriorityGwei), 'gwei'),
        };
    }
    return {
        gasPrice: feeData?.gasPrice || ethers.utils.parseUnits(String(fallbackPriorityGwei), 'gwei'),
    };
}

function applyBundlePremium(fees, multiplier = 150) {
    if (!fees || typeof fees !== 'object') return fees;
    const m = ethers.BigNumber.from(multiplier);
    const d = ethers.BigNumber.from(100);
    if (fees.maxFeePerGas) {
        return {
            ...fees,
            maxFeePerGas: ethers.BigNumber.from(fees.maxFeePerGas).mul(m).div(d),
            maxPriorityFeePerGas: ethers.BigNumber.from(fees.maxPriorityFeePerGas || 0).mul(m).div(d),
        };
    }
    if (fees.gasPrice) {
        return { ...fees, gasPrice: ethers.BigNumber.from(fees.gasPrice).mul(m).div(d) };
    }
    return fees;
}

function signedRawTxHash(raw) {
    const txData = raw.startsWith('0x') ? raw.slice(2) : raw;
    return ethers.utils.keccak256('0x' + txData);
}

function getBundleRelayMode() {
    const mode = String(getConfig()?.network?.bundleRelay || 'blockrazor').toLowerCase();
    if (mode === 'disabled') return 'disabled';
    if (mode === 'flashbots') return 'flashbots';
    return 'blockrazor';
}

/** POST signed bundle to Node `/api/send-bundle` (server forwards to BlockRazor or Flashbots — avoids browser CORS, logs appear in terminal). */
async function postBundleToBackendApi(signedTxs, provider, revertingTxHashes, { blockrazorOnly = false } = {}) {
    const url = `${API_URL}/send-bundle`;
    let response;
    try {
        response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                txs: signedTxs,
                revertingTxHashes,
                ...(blockrazorOnly ? { blockrazorOnly: true } : {}),
            }),
        });
    } catch (err) {
        const msg = err?.message || String(err);
        console.error('[bundle] POST to backend failed:', msg, url);
        if (msg === 'Failed to fetch' || /failed to fetch|network|load failed/i.test(msg)) {
            throw new Error(
                `Cannot reach bundle API (${url}). Check backend is running and runtime serverUrl matches this app (same host/port as API). Logs: watch the terminal where node backend/server.js runs.`,
            );
        }
        throw err;
    }
    const text = await response.text();
    let data = {};
    try {
        data = text ? JSON.parse(text) : {};
    } catch {
        data = {};
    }
    if (!response.ok) {
        throw new Error(data.error || data.details || `Bundle relay failed (HTTP ${response.status})`);
    }
    if (data.error && !data.bundleHash && !data.ok) {
        throw new Error(typeof data.error === 'string' ? data.error : JSON.stringify(data.error));
    }
    console.log('[bundle] relay OK:', {
        numberOfTxs: signedTxs.length,
        maxBlockNumber: data.maxBlockNumberDefault ? '(BlockRazor default)' : (data.maxBlock ?? null),
        bundleHash: data.bundleHash || null,
    });
    return data;
}

/**
 * Submit signed txs via backend `/api/send-bundle` → BlockRazor or Flashbots.
 * @param {object} [options]
 * @param {number} [options.revertingFromIndex] – txs from this index onward are passed as `revertingTxHashes` (Enable + Bundle uses 1 so openTrading is not revert-tagged).
 * @param {string[]} [options.revertingTxHashes] – explicit list; overrides revertingFromIndex.
 */
async function sendBundleRelay(signedTxs, provider, options = {}) {
    const mode = getBundleRelayMode();
    if (mode === 'disabled') {
        throw new Error('Bundle relay is disabled by admin (platform settings)');
    }

    let revertingTxHashes = options.revertingTxHashes;
    if (!Array.isArray(revertingTxHashes)) {
        const from = Number(options.revertingFromIndex);
        if (Number.isFinite(from) && from > 0) {
            revertingTxHashes = signedTxs.slice(from).map(signedRawTxHash);
        } else {
            revertingTxHashes = signedTxs.map(signedRawTxHash);
        }
    }

    return await postBundleToBackendApi(signedTxs, provider, revertingTxHashes, {
        blockrazorOnly: options.blockrazorOnly === true,
    });
}

class EthereumService {
    constructor() {
        this.initialized = false;
        this.getAuthToken = null;
        this.getProjectId = null;
        this.masterWallet = null;
        this.fundingWallet = null;
    }

    setAuthTokenGetter(getter) {
        this.getAuthToken = getter;
    }

    setProjectIdGetter(getter) {
        this.getProjectId = getter;
    }

    async getAuthHeaders() {
        try {
            if (!this.getAuthToken) return {};
            const token = await this.getAuthToken();
            if (!token) return {};
            return { Authorization: `Bearer ${token}` };
        } catch (e) {
            console.warn('Failed to get auth token:', e); // eslint-disable-line
            return {};
        }
    }

    async getProjectHeaders() {
        try {
            if (!this.getProjectId) return {};
            const projectId = await this.getProjectId();
            if (!projectId) return {};
            return { 'X-Project-Id': projectId };
        } catch (e) {
            return {};
        }
    }

    async initialize() {
        try {
            const config = getConfig();
            console.log('Initializing with config:', {
                rpcUrl: config.network.rpcUrl,
                hasMainKey: !!config.network.mainPrivateKey,
                hasFundingKey: !!config.network.fundingPrivateKey
            });

            // Initialize provider with RPC failover.
            // Many public ETH RPCs intermittently return 429/-32603 or block browsers (CORS),
            // which causes ethers v5 to throw "could not detect network".
            const chainId = Number(config?.network?.chainId || 1);
            const network = { name: chainId === 1 ? 'homestead' : `chain-${chainId}`, chainId };

            const candidates = [
                config.network.rpcUrl,
                // Known-good public fallbacks
                'https://ethereum-rpc.publicnode.com',
                'https://rpc.ankr.com/eth',
                'https://cloudflare-eth.com',
            ]
                .map((u) => (u ? String(u).trim() : ''))
                .filter(Boolean);

            const withTimeout = (p, ms, label) => {
                let t = null;
                const timeout = new Promise((_, reject) => {
                    t = setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms);
                });
                return Promise.race([p, timeout]).finally(() => {
                    if (t) clearTimeout(t);
                });
            };

            let lastErr = null;
            for (const url of candidates) {
                try {
                    const provider = new ethers.providers.JsonRpcProvider(url, network);
                    // Probe quickly so we don't hang on a dead RPC
                    await withTimeout(provider.getBlockNumber(), 8000, `RPC ${url}`);
                    this.provider = provider;
                    if (url !== config.network.rpcUrl) {
                        console.warn('RPC failover: using fallback RPC', url); // eslint-disable-line
                    }
                    break;
                } catch (e) {
                    lastErr = e;
                    continue;
                }
            }

            if (!this.provider) {
                throw new Error(lastErr?.message || 'No RPC endpoint is reachable');
            }
            
            // Load per-user master wallet from backend (Clerk auth)
            const authHeaders = await this.getAuthHeaders();
            const projectHeaders = await this.getProjectHeaders();
            if (!authHeaders.Authorization) {
                throw new Error('Not signed in');
            }
            const exportRes = await fetch(`${API_URL}/me/master-wallet/export`, {
                headers: { ...authHeaders, ...projectHeaders }
            });
            if (!exportRes.ok) {
                const err = await exportRes.json().catch(() => ({}));
                throw new Error(err.error || 'No master wallet found. Generate or import one.');
            }
            const exported = await exportRes.json();
            const mainPrivateKey = exported.privateKey;

            // Use master wallet for funding too (until a separate per-user funding wallet is introduced)
            this.masterWallet = new ethers.Wallet(mainPrivateKey, this.provider);
            this.fundingWallet = this.masterWallet;
            
            // Set contract addresses from config
            this.tokenAddress = config.network.tokenAddress;
            this.routerAddress = config.network.routerAddress;
            this.WCOIN = config.network.nativeCoin;
            this.decimals = config.token.decimals;
            this.tokenMainAbi = [
                "function openTrading() external",
                "function tradingEnabled() view returns (bool)",
            ];

            // Store gas config
            this.gasConfig = config.gas;
            this.transactionConfig = config.transaction;

            this.initialized = true;
            console.log('EthereumService initialized successfully');
        } catch (error) {
            console.error('Failed to initialize EthereumService:', error);
            this.initialized = false;
            // Important: do not throw here, because this service is imported as a singleton.
            // Callers that need it should use ensureInitialized() and handle errors there.
            return;
        }
    }

    async reloadConfig() {
        console.log('Reloading configuration...');
        this.initialized = false;
        await this.initialize();
        console.log('Configuration reloaded successfully');
    }

    async ensureInitialized() {
        if (!this.initialized) {
            await this.initialize();
            if (!this.initialized) {
                throw new Error('EthereumService not initialized. Sign in and generate/import a master wallet.');
            }
        }
    }

    async saveWallets(wallets) {
        await this.ensureInitialized();
        try {
            const projectId = await this.getProjectId?.().catch?.(() => null);
            console.log('Saving wallets:', wallets);
            const authHeaders = await this.getAuthHeaders();
            const projectHeaders = await this.getProjectHeaders();
            const response = await fetch(`${API_URL}/wallets`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...authHeaders,
                    ...projectHeaders,
                },
                body: JSON.stringify(wallets),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(data?.error || `Failed to save wallets (${response.status})`);
            }
            if (!data?.success) {
                throw new Error(data?.error || 'Failed to save wallets');
            }
            // Also update localStorage for immediate UI updates
            localStorage.setItem(projectId ? `wallets.${projectId}` : 'wallets', JSON.stringify(wallets));
            try {
                addRecoverySnapshot({
                    projectId,
                    type: 'wallets',
                    label: `Wallets saved (${Array.isArray(wallets) ? wallets.length : 0})`,
                    data: { wallets },
                });
            } catch {
                // ignore snapshot errors
            }
            console.log('Wallets saved successfully');
            return { success: true, persisted: 'server' };
        } catch (error) {
            console.error('Error saving wallets:', error);
            const projectId = await this.getProjectId?.().catch?.(() => null);
            // Fallback to localStorage if API fails
            localStorage.setItem(projectId ? `wallets.${projectId}` : 'wallets', JSON.stringify(wallets));
            try {
                addRecoverySnapshot({
                    projectId,
                    type: 'wallets',
                    label: `Wallets saved locally (${Array.isArray(wallets) ? wallets.length : 0})`,
                    data: { wallets, error: error?.message || String(error) },
                });
            } catch {
                // ignore snapshot errors
            }
            console.log('Wallets saved to localStorage as fallback');
            return { success: false, persisted: 'local', error: error?.message || String(error) };
        }
    }

    async loadWallets(options = {}) {
        await this.ensureInitialized();
        const { allowLocalFallback = true } = options || {};
        try {
            console.log('Loading wallets from API...');
            const authHeaders = await this.getAuthHeaders();
            const projectHeaders = await this.getProjectHeaders();
            const response = await fetch(`${API_URL}/wallets`, {
                headers: {
                    ...authHeaders,
                    ...projectHeaders,
                },
            });
            if (!response.ok) {
                throw new Error(`Failed to load wallets: ${response.statusText}`);
            }
            const wallets = await response.json();
            console.log('Wallets loaded from API:', wallets);

            if (!Array.isArray(wallets)) {
                console.warn('Invalid wallet data from API');
                return [];
            }

            // Update balances for all wallets (native + token)
            const config = getConfig();
            const tokenAddress = config?.network?.tokenAddress;
            const tokenContract = tokenAddress
                ? new ethers.Contract(tokenAddress, tokenABI, this.provider)
                : null;
            const tokenDecimals = config?.token?.decimals ?? 18;

            const updatedWallets = await Promise.all(
                wallets.map(async (wallet) => {
                    try {
                        const [balance, tokenBal] = await Promise.all([
                            this.provider.getBalance(wallet.address),
                            tokenContract ? tokenContract.balanceOf(wallet.address).catch(() => ethers.BigNumber.from(0)) : Promise.resolve(ethers.BigNumber.from(0)),
                        ]);
                        return {
                            ...wallet,
                            balance: ethers.utils.formatEther(balance),
                            tokenBalance: tokenContract ? ethers.utils.formatUnits(tokenBal, tokenDecimals) : '0'
                        };
                    } catch (error) {
                        console.error(`Error getting balance for wallet ${wallet.address}:`, error);
                        return {
                            ...wallet,
                            balance: '0.00',
                            tokenBalance: '0'
                        };
                    }
                })
            );

            console.log('Wallets with updated balances:', updatedWallets);
            return updatedWallets;
        } catch (error) {
            console.error('Error loading wallets:', error);
            if (!allowLocalFallback) {
                throw error;
            }
            // Try to load from localStorage as fallback (legacy behavior)
            try {
                const projectId = await this.getProjectId?.().catch?.(() => null);
                const storedWallets = localStorage.getItem(projectId ? `wallets.${projectId}` : 'wallets');
                if (storedWallets) {
                    console.log('Loading wallets from localStorage');
                    const parsedWallets = JSON.parse(storedWallets);
                    if (Array.isArray(parsedWallets)) {
                        return parsedWallets;
                    }
                }
            } catch (localStorageError) {
                console.error('Error loading from localStorage:', localStorageError);
            }
            return [];
        }
    }

    async createWallets(count) {
        await this.ensureInitialized();
        try {
            console.log('Creating wallets...');
            const wallets = [];
            for (let i = 0; i < count; i++) {
                const wallet = ethers.Wallet.createRandom();
                wallets.push({ 
                    address: wallet.address, 
                    privateKey: wallet.privateKey,
                    balance: '0.00'
                });
            }
            
            console.log('Created wallets:', wallets);
            await this.saveWallets(wallets);
            return wallets;
        } catch (error) {
            console.error('Error creating wallets:', error);
            throw error;
        }
    }

    async fundWallets(wallets, amount) {
        await this.ensureInitialized();
        try {
            const results = [];
            const fundingTransactions = [];
            const baseGasPrice = ethers.utils.parseUnits(this.gasConfig.normalGwei, 'gwei');
            
            // Check funding wallet balance first
            const fundingBalance = await this.provider.getBalance(this.fundingWallet.address);
            console.log('Funding wallet balance:', ethers.utils.formatEther(fundingBalance), 'BNB');
            
            // Calculate gas cost and ensure we have enough balance
            const gasCost = baseGasPrice.mul(21000); // Standard gas limit for simple transfers
            const transferAmount = ethers.utils.parseEther(amount.toString()); // Use specified amount
            const totalCost = transferAmount.add(gasCost);
            
            // Check if we have enough balance for all transactions
            const totalNeeded = totalCost.mul(wallets.length);
            if (fundingBalance.lt(totalNeeded)) {
                throw new Error(`Insufficient balance. Need ${ethers.utils.formatEther(totalNeeded)} BNB but have ${ethers.utils.formatEther(fundingBalance)} BNB`);
            }
            
            console.log('Amount per wallet:', ethers.utils.formatEther(transferAmount), 'BNB');
            
            const feeData = await this.provider.getFeeData().catch(() => null);
            const transferFees = applyBundlePremium(pickFeeData(feeData, '2'), 200);

            // Get current nonce and chainId for funding wallet
            let fundingNonce = await this.fundingWallet.getTransactionCount('pending');
            const chainId = await this.provider.getNetwork().then(n => n.chainId);
            console.log('Current nonce:', fundingNonce, 'Chain ID:', chainId);
            
            // Prepare transactions for each wallet
            for (let i = 0; i < wallets.length; i++) {
                try {
                    console.log(`Preparing transaction ${i + 1} to wallet ${wallets[i].address}`);
                    
                    const tx = {
                        to: wallets[i].address,
                        value: transferAmount,
                        gasLimit: ethers.BigNumber.from(21000),
                        nonce: fundingNonce + i,
                        chainId: chainId,
                        ...transferFees,
                    };

                    console.log('Transaction details:', {
                        to: tx.to,
                        value: ethers.utils.formatEther(tx.value),
                        gasLimit: tx.gasLimit.toString(),
                        nonce: tx.nonce,
                        chainId: tx.chainId
                    });

                    // Sign transaction with funding wallet
                    const signedTx = await this.fundingWallet.signTransaction(tx);
                    console.log('Transaction signed successfully');
                    
                    if (signedTx) {
                        fundingTransactions.push(signedTx);
                        console.log(`Transaction ${i + 1} added to bundle`);
                    } else {
                        console.error(`Failed to sign transaction ${i + 1}`);
                    }

                    results.push({
                        address: wallets[i].address,
                        success: true,
                        amount: ethers.utils.formatEther(transferAmount)
                    });
                } catch (error) {
                    console.error(`Error preparing transaction ${i + 1}:`, error.message);
                    console.error('Full error:', error);
                }
            }

            // Validate bundle
            if (fundingTransactions.length === 0) {
                console.error('No transactions were successfully signed');
                throw new Error('No valid transactions for bundle. Check console for details.');
            }

            console.log('Bundle prepared with', fundingTransactions.length, 'transactions');

            // Send bundle (Disperse seed / fund many — always BlockRazor, not platform Flashbots relay)
            console.log('Sending fund bundle via BlockRazor...');
            const bundleResult = await sendBundleRelay(fundingTransactions, this.provider, {
                blockrazorOnly: true,
                revertingTxHashes: [],
            });
            if (bundleResult.error) {
                console.error('Bundle relay error:', bundleResult.error);
                return { success: false, error: bundleResult.error.message };
            }

            await waitForSignedTransactionsMined(this.provider, fundingTransactions).catch((e) => {
                console.warn('Fund wallets: awaiting receipts:', e?.message || e);
            });

            // Update wallet balances after successful funding
            const updatedWallets = await Promise.all(
                wallets.map(async (wallet) => {
                    const balance = await this.provider.getBalance(wallet.address);
                    return {
                        ...wallet,
                        balance: ethers.utils.formatEther(balance)
                    };
                })
            );
            
            await this.saveWallets(updatedWallets);
            
            return { 
                success: true, 
                bundleHash: bundleResult?.bundleHash ?? null,
                hash: bundleResult?.bundleHash || bundleResult?.result || null,
                results,
                numberOfTransactions: fundingTransactions.length
            };
        } catch (error) {
            console.error('Fund wallets error:', error.message);
            console.error('Full error:', error);
            return { success: false, error: error.message };
        }
    }

    async distributeFunds(wallets, { minAmountEth = 0 } = {}) {
        await this.ensureInitialized();
        try {
            const list = Array.isArray(wallets) ? wallets : [];
            const clean = list
                .map((w) => {
                    const rawFund = w?.fundAmount;
                    const fund = typeof rawFund === 'number' ? rawFund : typeof rawFund === 'string' ? parseFloat(rawFund) : NaN;
                    const fundEth = Number.isFinite(fund) ? fund : 0;
                    // fundAmount is the TOTAL to send. Back-compat: if fundAmount missing, use buyAmount.
                    const rawBuy = w?.buyAmount;
                    const buy = typeof rawBuy === 'number' ? rawBuy : typeof rawBuy === 'string' ? parseFloat(rawBuy) : NaN;
                    const buyEth = Number.isFinite(buy) ? buy : 0;
                    const totalEth = fundEth > 0 ? fundEth : buyEth;
                    return {
                        wallet: w,
                        amountEth: totalEth,
                    };
                })
                .filter((x) => x.wallet?.address && x.amountEth > 0 && x.amountEth >= minAmountEth);

            if (clean.length === 0) {
                return { success: false, error: 'No wallets with a valid amount to fund.' };
            }

            // Follow ETH_MIXER method: use Disperse contract to send native funds
            // to many recipients in one transaction.
            const cfg = getConfig();
            const disperseAddress = cfg?.network?.disperseAddress;
            if (!disperseAddress || !ethers.utils.isAddress(disperseAddress)) {
                throw new Error('Disperse contract address is missing/invalid (set in Admin → Platform settings)');
            }

            const disperseABI = [
                'function disperseEther(address[] recipients, uint256[] values) payable',
            ];
            const disperse = new ethers.Contract(disperseAddress, disperseABI, this.fundingWallet);

            const recipients = clean.map((x) => x.wallet.address);
            const valuesWei = clean.map((x) => ethers.utils.parseEther(x.amountEth.toString()));
            const totalTransferWei = valuesWei.reduce((sum, v) => sum.add(v), ethers.BigNumber.from(0));

            const fundingBalance = await this.provider.getBalance(this.fundingWallet.address);
            if (fundingBalance.lt(totalTransferWei)) {
                throw new Error(
                    `Insufficient balance. Need ${ethers.utils.formatEther(totalTransferWei)} ETH but have ${ethers.utils.formatEther(fundingBalance)} ETH`
                );
            }

            // Chunk to avoid block gas limits on very large lists.
            const MAX_RECIPIENTS_PER_TX = 150;
            const batches = [];
            for (let i = 0; i < recipients.length; i += MAX_RECIPIENTS_PER_TX) {
                batches.push({
                    recipients: recipients.slice(i, i + MAX_RECIPIENTS_PER_TX),
                    valuesWei: valuesWei.slice(i, i + MAX_RECIPIENTS_PER_TX),
                });
            }

            const allResults = [];
            let totalSentWei = ethers.BigNumber.from(0);
            const network = await this.provider.getNetwork();
            const feeDataBR = await this.provider.getFeeData().catch(() => null);
            const disperseFees = applyBundlePremium(pickFeeData(feeDataBR, '2'), 200);
            let disperseNonce = await this.fundingWallet.getTransactionCount('pending');

            const signedDisperses = [];
            for (const batch of batches) {
                const batchTotalWei = batch.valuesWei.reduce((sum, v) => sum.add(v), ethers.BigNumber.from(0));
                totalSentWei = totalSentWei.add(batchTotalWei);

                for (let j = 0; j < batch.recipients.length; j++) {
                    allResults.push({
                        address: batch.recipients[j],
                        success: true,
                        amount: ethers.utils.formatEther(batch.valuesWei[j]),
                    });
                }

                let populated = await disperse.populateTransaction.disperseEther(
                    batch.recipients,
                    batch.valuesWei,
                    { value: batchTotalWei },
                );
                populated.nonce = disperseNonce;
                populated.chainId = network.chainId;
                disperseNonce += 1;
                Object.assign(populated, disperseFees);

                try {
                    const gasEstimate = await this.provider.estimateGas({
                        ...populated,
                        from: this.fundingWallet.address,
                    });
                    populated.gasLimit = gasEstimate.mul(120).div(100);
                } catch (_) {
                    // noop
                }
                const dispFloor = ethers.BigNumber.from(String(this.gasConfig?.topGasLimit || '2500000'));
                if (!populated.gasLimit || ethers.BigNumber.from(populated.gasLimit).isZero()) {
                    populated.gasLimit = dispFloor;
                }
                const signedDisp = await this.fundingWallet.signTransaction(populated);
                signedDisperses.push(signedDisp);
            }

            console.log('[distributeFunds] Disperse bundle(s) → BlockRazor, count:', signedDisperses.length);
            const disperseRelay = await sendBundleRelay(signedDisperses, this.provider, {
                blockrazorOnly: true,
                revertingTxHashes: [],
            });
            await waitForSignedTransactionsMined(this.provider, signedDisperses).catch((e) => {
                console.warn('[distributeFunds] awaiting mined receipts:', e?.message || e);
            });

            const txHashesParsed = signedDisperses.map((raw) => ethers.utils.parseTransaction(raw).hash);

            return {
                success: true,
                bundleHash: disperseRelay?.bundleHash ?? null,
                hash: txHashesParsed[0] || null,
                hashes: txHashesParsed,
                results: allResults,
                numberOfTransactions: signedDisperses.length,
                totalEth: ethers.utils.formatEther(totalSentWei),
                method: 'disperse-blockrazor',
                disperseAddress,
            };
        } catch (error) {
            console.error('Distribute funds error:', error?.message || error);
            return { success: false, error: error?.message || String(error) };
        }
    }

    /**
     * Refund-style bundle relay — always BlockRazor on the backend.
     * @param {{ allowReverts?: boolean }} [options] – default false = all txs must succeed (strict bundle).
     */
    async sendSignedTransactionsViaFlashbots(signedTxs, options = {}) {
        await this.ensureInitialized();
        if (!Array.isArray(signedTxs) || signedTxs.length === 0) {
            throw new Error('No signed transactions provided');
        }
        const relay = { blockrazorOnly: true };
        if (Object.prototype.hasOwnProperty.call(options, 'revertingTxHashes')) {
            relay.revertingTxHashes = options.revertingTxHashes;
        } else if (options.allowReverts !== true) {
            relay.revertingTxHashes = [];
        }
        return await sendBundleRelay(signedTxs, this.provider, relay);
    }

    /**
     * Bulk approve for sell‑all — one BlockRazor bundle (`blockrazorOnly`), every tx must succeed.
     */
    async bundleSellApprovePhase(walletRows, percentStr, { creationPrivateKey = '' } = {}) {
        await this.ensureInitialized();
        const pct = Number(percentStr);
        if (!Number.isFinite(pct) || pct <= 0 || pct > 100) throw new Error('Invalid percent');

        const feeData = await this.provider.getFeeData().catch(() => null);
        const feeOptsBase = applyBundlePremium(pickFeeData(feeData, '2'), 200);

        const signed = [];

        const rows = Array.isArray(walletRows) ? walletRows : [];
        for (const row of rows) {
            const pkCreation = String(creationPrivateKey || '').trim();
            const pkRow = String(row?.privateKey || '').trim();
            const pkRaw = row?._kind === 'creation' ? pkCreation : pkRow;
            if (!pkRaw) {
                if (row?._kind === 'creation') {
                    throw new Error('Creation wallet private key is missing (export failed)');
                }
                continue;
            }
            const normalized = pkRaw.startsWith('0x') ? pkRaw : `0x${pkRaw}`;
            const userWallet = new ethers.Wallet(normalized, this.provider);
            if (
                row?.address &&
                userWallet.address.toLowerCase() !== String(row.address).toLowerCase()
            ) {
                throw new Error(`Private key mismatch for wallet ${String(row.address).slice(0, 10)}…`);
            }

            const tokenContract = new ethers.Contract(this.tokenAddress, tokenABI, userWallet);

            const bal = await tokenContract.balanceOf(userWallet.address);
            if (bal.isZero()) continue;

            const amountIn = bal.mul(Math.floor(Math.min(100, Math.max(0, pct)) * 10000)).div(1000000);
            if (!amountIn || amountIn.isZero()) continue;

            const allowance = await tokenContract
                .allowance(userWallet.address, this.routerAddress)
                .catch(() => ethers.constants.Zero);
            if (!allowance.lt(amountIn)) continue;

            let populated = await tokenContract.populateTransaction.approve(
                this.routerAddress,
                ethers.constants.MaxUint256,
            );
            const nonce = await userWallet.getTransactionCount('pending');
            const chainId = (await this.provider.getNetwork()).chainId;
            Object.assign(populated, feeOptsBase, { nonce, chainId });

            try {
                const est = await this.provider.estimateGas({ ...populated, from: userWallet.address });
                populated.gasLimit = est.mul(12).div(10);
            } catch (_) {
                /* noop */
            }
            const gasFloorAp = ethers.BigNumber.from(String(this.gasConfig?.normalGasLimit || '100000'));
            if (!populated.gasLimit || ethers.BigNumber.from(populated.gasLimit).isZero()) {
                populated.gasLimit = gasFloorAp;
            }

            signed.push(await userWallet.signTransaction(populated));
        }

        if (signed.length === 0) return { success: true, skippedAll: true, count: 0 };

        console.log('[sell-all] approve bundle → BlockRazor, txs:', signed.length);
        const rel = await sendBundleRelay(signed, this.provider, { blockrazorOnly: true, revertingTxHashes: [] });
        await waitForSignedTransactionsMined(this.provider, signed);
        return { success: true, count: signed.length, bundleHash: rel?.bundleHash ?? null };
    }

    /**
     * Bulk swap for sell‑all — BlockRazor bundle after approve phase confirmations.
     */
    async bundleSellSwapPhase(walletRows, percentStr, { creationPrivateKey = '' } = {}) {
        await this.ensureInitialized();
        const pct = Number(percentStr);
        if (!Number.isFinite(pct) || pct <= 0 || pct > 100) throw new Error('Invalid percent');

        const feeData = await this.provider.getFeeData().catch(() => null);
        const feeOptsBase = applyBundlePremium(pickFeeData(feeData, '2'), 200);

        const path = [this.tokenAddress, this.WCOIN];
        const deadline = Math.floor(Date.now() / 1000) + 60 * 20;

        const signed = [];

        const rows = Array.isArray(walletRows) ? walletRows : [];
        for (const row of rows) {
            const pkCreation = String(creationPrivateKey || '').trim();
            const pkRow = String(row?.privateKey || '').trim();
            const pkRaw = row?._kind === 'creation' ? pkCreation : pkRow;
            if (!pkRaw) {
                if (row?._kind === 'creation') {
                    throw new Error('Creation wallet private key is missing (export failed)');
                }
                continue;
            }
            const normalized = pkRaw.startsWith('0x') ? pkRaw : `0x${pkRaw}`;
            const userWallet = new ethers.Wallet(normalized, this.provider);
            if (
                row?.address &&
                userWallet.address.toLowerCase() !== String(row.address).toLowerCase()
            ) {
                throw new Error(`Private key mismatch for wallet ${String(row.address).slice(0, 10)}…`);
            }

            const tokenContract = new ethers.Contract(this.tokenAddress, tokenABI, userWallet);
            const routerC = new ethers.Contract(this.routerAddress, routerABI, userWallet);

            const bal = await tokenContract.balanceOf(userWallet.address);
            if (bal.isZero()) continue;

            const amountIn = bal.mul(Math.floor(Math.min(100, Math.max(0, pct)) * 10000)).div(1000000);
            if (!amountIn || amountIn.isZero()) continue;

            const allowance = await tokenContract
                .allowance(userWallet.address, this.routerAddress)
                .catch(() => ethers.constants.Zero);
            if (allowance.lt(amountIn)) {
                throw new Error(
                    `Allowance too low for ${userWallet.address.slice(0, 10)}… — complete approve bundle first`,
                );
            }

            let populated = await routerC.populateTransaction.swapExactTokensForETHSupportingFeeOnTransferTokens(
                amountIn,
                0,
                path,
                userWallet.address,
                deadline,
            );
            const nonce = await userWallet.getTransactionCount('pending');
            const chainId = (await this.provider.getNetwork()).chainId;
            Object.assign(populated, feeOptsBase, { nonce, chainId });

            try {
                const est = await this.provider.estimateGas({ ...populated, from: userWallet.address });
                populated.gasLimit = est.mul(12).div(10);
            } catch (_) {
                /* noop */
            }
            const gasFloorSw = ethers.BigNumber.from(String(this.gasConfig?.normalGasLimit || '350000'));
            if (!populated.gasLimit || ethers.BigNumber.from(populated.gasLimit).isZero()) {
                populated.gasLimit = gasFloorSw;
            }

            signed.push(await userWallet.signTransaction(populated));
        }

        if (signed.length === 0) return { success: true, skippedAll: true, count: 0 };

        console.log('[sell-all] swap bundle → BlockRazor, txs:', signed.length);
        const rel = await sendBundleRelay(signed, this.provider, { blockrazorOnly: true, revertingTxHashes: [] });
        await waitForSignedTransactionsMined(this.provider, signed);
        return { success: true, count: signed.length, bundleHash: rel?.bundleHash ?? null };
    }

    async addLiquidity(amount, { mode = 'public' } = {}) {
        await this.ensureInitialized();
        if (!this.tokenAddress || !ethers.utils.isAddress(this.tokenAddress)) {
            return { success: false, error: 'No project token is set. Deploy/assign a token first (Launch Wizard → Deploy token).' };
        }
        if (!amount || isNaN(amount) || Number(amount) <= 0) {
            throw new Error('Invalid amount provided');
        }

        const deadline = Math.floor(Date.now() / 1000) + (this.transactionConfig.deadlineMinutes * 60);

        try {
            // Liquidity MUST be added by the creation wallet (C‑Master).
            const authHeaders = await this.getAuthHeaders();
            const projectHeaders = await this.getProjectHeaders();
            const exportRes = await fetch(`${API_URL}/me/creation-wallet/export`, {
                headers: { ...authHeaders, ...projectHeaders }
            });
            if (!exportRes.ok) {
                const err = await exportRes.json().catch(() => ({}));
                throw new Error(err?.error || 'Creation wallet not found. Generate/import one first (Launch Wizard).');
            }
            const exported = await exportRes.json();
            const pk = (exported?.privateKey || '').toString().trim();
            if (!pk) throw new Error('Failed to export creation wallet key');
            const normalized = pk.startsWith('0x') ? pk : `0x${pk}`;
            const creationWallet = new ethers.Wallet(normalized, this.provider);

            const router = new ethers.Contract(this.routerAddress, routerABI, creationWallet);
            const tokenContract = new ethers.Contract(this.tokenAddress, tokenABI, creationWallet);
            const network = await this.provider.getNetwork();
            const feeData = await this.provider.getFeeData().catch(() => null);

            // Token side is ALWAYS 100% of total supply (fixed requirement).
            const totalSupplyWei = await tokenContract.totalSupply();
            const creationBalWei = await tokenContract.balanceOf(creationWallet.address);
            if (creationBalWei.lt(totalSupplyWei)) {
                return {
                    success: false,
                    error:
                        `Creation wallet does not hold full total supply. ` +
                        `Balance=${ethers.utils.formatUnits(creationBalWei, this.decimals)} ` +
                        `TotalSupply=${ethers.utils.formatUnits(totalSupplyWei, this.decimals)}.`
                };
            }

            const amountTokenDesired = totalSupplyWei;
            // For simplicity and to avoid revert due to rounding/slippage, accept any amounts.
            const amountTokenMin = ethers.BigNumber.from(0);
            const ethAmount = ethers.utils.parseEther(String(amount));
            const amountEthMin = ethers.BigNumber.from(0);

            const autoFees = pickFeeData(feeData, '2');

            // Get current nonce
            const nonce = await creationWallet.getTransactionCount('pending');

            if (mode === 'public') {
                // Simple: approve then add liquidity as normal txs, let the network choose fees.
                const approveResp = await tokenContract.approve(this.routerAddress, ethers.constants.MaxUint256, {
                    nonce,
                    ...autoFees,
                });
                // Wait for approval to be mined before sending addLiquidity.
                await withTimeout(approveResp.wait(1), 120_000, 'approve confirmation');
                const addResp = await router.addLiquidityETH(
                    this.tokenAddress,
                    amountTokenDesired,
                    amountTokenMin,
                    amountEthMin,
                    creationWallet.address,
                    deadline,
                    {
                        value: ethAmount,
                        nonce: nonce + 1,
                        ...autoFees,
                    }
                );

                return {
                    success: true,
                    mode: 'public',
                    hashes: [approveResp.hash, addResp.hash],
                    approveHash: approveResp.hash,
                    addLiquidityHash: addResp.hash,
                };
            }

            // Bundle mode: we must sign explicit transactions. Use provider feeData.
            const approveTx = await tokenContract.populateTransaction.approve(this.routerAddress, ethers.constants.MaxUint256);
            approveTx.nonce = nonce;
            approveTx.chainId = network.chainId;
            Object.assign(approveTx, autoFees);

            const addLiquidityTx = await router.populateTransaction.addLiquidityETH(
                this.tokenAddress,
                amountTokenDesired,
                amountTokenMin,
                amountEthMin,
                creationWallet.address,
                deadline,
                { value: ethAmount }
            );
            addLiquidityTx.nonce = nonce + 1;
            addLiquidityTx.chainId = network.chainId;
            Object.assign(addLiquidityTx, autoFees);

            console.log('Preparing transactions:', {
                approveTx: {
                    to: approveTx.to,
                    data: approveTx.data.slice(0, 66) + '...',
                    nonce: approveTx.nonce,
                    gasPrice: approveTx.gasPrice ? ethers.utils.formatUnits(approveTx.gasPrice, 'gwei') + ' gwei' : undefined,
                    maxFeePerGas: approveTx.maxFeePerGas ? ethers.utils.formatUnits(approveTx.maxFeePerGas, 'gwei') + ' gwei' : undefined,
                    approveAmount: ethers.utils.formatUnits(amountTokenDesired, this.decimals)
                },
                addLiquidityTx: {
                    to: addLiquidityTx.to,
                    data: addLiquidityTx.data.slice(0, 66) + '...',
                    nonce: addLiquidityTx.nonce,
                    tokenAmount: ethers.utils.formatUnits(amountTokenDesired, this.decimals),
                    tokenMin: ethers.utils.formatUnits(amountTokenMin, this.decimals),
                    ethAmount: ethers.utils.formatEther(ethAmount),
                    gasPrice: addLiquidityTx.gasPrice ? ethers.utils.formatUnits(addLiquidityTx.gasPrice, 'gwei') + ' gwei' : undefined,
                    maxFeePerGas: addLiquidityTx.maxFeePerGas ? ethers.utils.formatUnits(addLiquidityTx.maxFeePerGas, 'gwei') + ' gwei' : undefined
                }
            });

            const signedApproveTx = await creationWallet.signTransaction(approveTx);
            const signedAddLiquidityTx = await creationWallet.signTransaction(addLiquidityTx);

            if (mode === 'bundle') {
                const expectedTxHashes = [
                    ethers.utils.keccak256(signedApproveTx),
                    ethers.utils.keccak256(signedAddLiquidityTx),
                ];
                const result = await sendBundleRelay([signedApproveTx, signedAddLiquidityTx], this.provider);
                if (result?.error) {
                    const err =
                        typeof result.error === 'string'
                            ? result.error
                            : (result.error?.message || JSON.stringify(result.error));
                    return { success: false, error: err };
                }
                return {
                    success: true,
                    mode: 'bundle',
                    bundleHash: result?.bundleHash || result?.result || null,
                    expectedTxHashes,
                    receipt: result,
                };
            }

            return { success: false, error: 'Unknown liquidity mode' };
        } catch (error) {
            console.error('Add liquidity error:', error);
            return { success: false, error: error?.message || String(error) };
        }
    }

    /**
     * LaunchToken exposes `bool public tradingEnabled` → `tradingEnabled()` (no args).
     * When true, open trading has been enabled on-chain — UI should lock "Enable + Bundle".
     */
    async getLaunchTokenTradingEnabled(tokenAddr = null) {
        await this.ensureInitialized();
        const addr = (tokenAddr || this.tokenAddress || '').toString().trim();
        if (!addr || !ethers.utils.isAddress(addr)) return false;
        const c = new ethers.Contract(addr, ['function tradingEnabled() view returns (bool)'], this.provider);
        try {
            return await c.tradingEnabled();
        } catch {
            return false;
        }
    }

    async getUniswapV2PairAddress() {
        await this.ensureInitialized();
        if (!this.tokenAddress || !ethers.utils.isAddress(this.tokenAddress)) return null;
        try {
            const routerRO = new ethers.Contract(this.routerAddress, routerABI, this.provider);
            const factoryAddr = await routerRO.factory();
            const weth = await routerRO.WETH();
            const factory = new ethers.Contract(factoryAddr, factoryABI, this.provider);
            const p = await factory.getPair(this.tokenAddress, weth);
            if (!p || p === ethers.constants.AddressZero) return null;
            return p;
        } catch {
            return null;
        }
    }

    async getTxStatus(hash) {
        await this.ensureInitialized();
        if (!hash) return { status: 'missing' };
        const receipt = await this.provider.getTransactionReceipt(hash);
        if (!receipt) return { status: 'pending' };
        return {
            status: receipt.status === 1 ? 'success' : 'reverted',
            receipt,
        };
    }

    async enableTrading(customSwapAmounts = {}) {
        await this.ensureInitialized();
        const deadline = Math.floor(Date.now() / 1000) + (this.transactionConfig.deadlineMinutes * 60);
        const path = [this.WCOIN, this.tokenAddress];

        try {
            if (!this.tokenAddress || !ethers.utils.isAddress(this.tokenAddress)) {
                throw new Error('Token address is not set. Deploy token first (Launch Wizard).');
            }
            if (!this.routerAddress || !ethers.utils.isAddress(this.routerAddress)) {
                throw new Error('Router address is not configured.');
            }

            const feeData = await this.provider.getFeeData().catch(() => null);
            // For bundles, "auto" fees often aren't competitive enough.
            // Still derived from network feeData, but we apply a premium multiplier.
            const openFees = applyBundlePremium(pickFeeData(feeData, '2'), 200);
            const buyFees = applyBundlePremium(pickFeeData(feeData, '1'), 200);

            // Trading must be enabled by the creation wallet (token owner).
            const authHeaders = await this.getAuthHeaders();
            const projectHeaders = await this.getProjectHeaders();
            const exportRes = await fetch(`${API_URL}/me/creation-wallet/export`, {
                headers: { ...authHeaders, ...projectHeaders }
            });
            if (!exportRes.ok) {
                const err = await exportRes.json().catch(() => ({}));
                throw new Error(err?.error || 'Creation wallet not found. Generate/import one first (Launch Wizard).');
            }
            const exported = await exportRes.json();
            const pk = (exported?.privateKey || '').toString().trim();
            if (!pk) throw new Error('Failed to export creation wallet key');
            const normalized = pk.startsWith('0x') ? pk : `0x${pk}`;
            const creationWallet = new ethers.Wallet(normalized, this.provider);

            const tokenMain = new ethers.Contract(this.tokenAddress, this.tokenMainAbi, creationWallet);

            // Preflight: openTrading should succeed (otherwise the bundle will never include).
            // callStatic gives us the revert reason without sending anything.
            let openTradingPreflight = { ok: true };
            try {
                // Some tokens revert on repeated calls; this is the exact error we need surfaced.
                // eslint-disable-next-line no-await-in-loop
                await tokenMain.callStatic.openTrading({ from: creationWallet.address });
            } catch (e) {
                openTradingPreflight = { ok: false, error: e?.reason || e?.error?.message || e?.message || String(e) };
            }
            if (!openTradingPreflight.ok) {
                return {
                    success: false,
                    error: `openTrading() would revert: ${openTradingPreflight.error}`,
                };
            }

            // Preflight: liquidity must exist; otherwise swaps will revert even if trading is enabled.
            try {
                const pair = await this.getUniswapV2PairAddress();
                if (!pair) {
                    return { success: false, error: 'Liquidity not detected (pair not found). Add liquidity first.' };
                }
                const pairAbi = [
                    'function getReserves() external view returns (uint112 reserve0, uint112 reserve1, uint32 blockTimestampLast)',
                ];
                const pairC = new ethers.Contract(pair, pairAbi, this.provider);
                const [r0, r1] = await pairC.getReserves();
                if (ethers.BigNumber.from(r0).isZero() || ethers.BigNumber.from(r1).isZero()) {
                    return { success: false, error: 'Liquidity not detected (pair reserves are zero). Add liquidity first.' };
                }
            } catch {
                // ignore liquidity preflight errors (RPC quirks); proceed anyway
            }

            // Load wallets and their assigned values
            // IMPORTANT: bundling must use DB wallets only (no localStorage fallback),
            // otherwise we can accidentally bundle stale/static wallets.
            const wallets = await this.loadWallets({ allowLocalFallback: false });
            console.log('Loaded wallets:', wallets.map(w => ({
                address: w.address,
                balance: w.balance
            })));

            // Initialize wallets and check balances
            const buyerWallets = wallets.map(wallet => new ethers.Wallet(wallet.privateKey, this.provider));
            const buyerRouters = buyerWallets.map(wallet => new ethers.Contract(this.routerAddress, routerABI, wallet));

            // Get current balances for all wallets
            const walletBalances = await Promise.all(buyerWallets.map(async (wallet) => {
                const balance = await this.provider.getBalance(wallet.address);
                return {
                    address: wallet.address,
                    balance: ethers.utils.formatEther(balance)
                };
            }));

            console.log('Wallet balances before trading:', walletBalances);

            // Prepare creation-wallet tx (open trading)
            const creationNonce = await creationWallet.getTransactionCount('pending');
            const chainId = (await this.provider.getNetwork()).chainId;

            const openTradingTx = await tokenMain.populateTransaction.openTrading();
            openTradingTx.nonce = creationNonce;
            openTradingTx.chainId = chainId;
            Object.assign(openTradingTx, openFees);
            try {
                const est = await this.provider.estimateGas({ ...openTradingTx, from: creationWallet.address });
                openTradingTx.gasLimit = est.mul(12).div(10);
            } catch {
                // ignore; floor below — unsigned tx with no gasLimit signs as 0 gas and relays reject it
            }
            const openGasFloor = ethers.BigNumber.from(String(this.gasConfig?.topGasLimit || '500000'));
            if (!openTradingTx.gasLimit || ethers.BigNumber.from(openTradingTx.gasLimit).isZero()) {
                openTradingTx.gasLimit = openGasFloor;
            }

            const signedOpenTradingTx = await creationWallet.signTransaction(openTradingTx);

            // Prepare buy transactions for each wallet
            const buyTransactions = [];
            const signedBuyTxs = await Promise.all(buyerWallets.map(async (wallet, index) => {
                const balance = await this.provider.getBalance(wallet.address);
                // Use custom swap amount if provided, otherwise use default (balance - 0.001)
                const customAmount = customSwapAmounts[wallet.address];
                let swapAmount = customAmount
                    ? ethers.utils.parseEther(String(customAmount))
                    : balance.sub(ethers.utils.parseEther('0.001'));
                if (swapAmount.lt(ethers.constants.Zero)) swapAmount = ethers.constants.Zero;

                // Skip explicit 0 allocations early (prevents pointless reverts)
                const minSwap = ethers.utils.parseEther('0.00000001');
                if (swapAmount.lt(minSwap)) {
                    buyTransactions.push({
                        wallet: wallet.address,
                        skipped: true,
                        reason: 'zero_amount',
                        balance: ethers.utils.formatEther(balance),
                        requestedSwapAmount: ethers.utils.formatEther(swapAmount),
                        finalSwapAmount: '0',
                    });
                    return null;
                }

                console.log(`Preparing buy transaction for wallet ${wallet.address}:`, {
                    balance: ethers.utils.formatEther(balance),
                    swapAmount: ethers.utils.formatEther(swapAmount),
                    customAmount: customAmount
                });

                const swapTx = await buyerRouters[index].populateTransaction.swapExactETHForTokens(
                    0, // amountOutMin
                    path,
                    wallet.address,
                    deadline,
                    {
                        value: swapAmount
                    }
                );
                swapTx.nonce = await wallet.getTransactionCount('pending');
                swapTx.chainId = (await this.provider.getNetwork()).chainId;
                Object.assign(swapTx, buyFees);
                try {
                    const est = await this.provider.estimateGas({ ...swapTx, from: wallet.address });
                    swapTx.gasLimit = est.mul(12).div(10);
                } catch {
                    // ignore; floor below — same as openTrading, missing gasLimit becomes 0 on sign
                }
                const swapGasFloor = ethers.BigNumber.from(String(this.gasConfig?.normalGasLimit || '300000'));
                if (!swapTx.gasLimit || ethers.BigNumber.from(swapTx.gasLimit).isZero()) {
                    swapTx.gasLimit = swapGasFloor;
                }

                // Ensure the wallet can actually afford (value + gas). If not, cap the swap amount.
                try {
                    const gasPriceLike = swapTx.maxFeePerGas || swapTx.gasPrice;
                    const gasLimitLike = swapTx.gasLimit || ethers.BigNumber.from(200000);
                    const gasCost = gasPriceLike ? ethers.BigNumber.from(gasPriceLike).mul(gasLimitLike) : ethers.BigNumber.from(0);
                    const dust = ethers.utils.parseEther('0.00000001');
                    const maxAffordableValue = balance.sub(gasCost).sub(dust);
                    if (maxAffordableValue.lte(ethers.constants.Zero)) {
                        buyTransactions.push({
                            wallet: wallet.address,
                            skipped: true,
                            reason: 'insufficient_for_gas',
                            balance: ethers.utils.formatEther(balance),
                            requestedSwapAmount: ethers.utils.formatEther(swapAmount),
                            finalSwapAmount: '0',
                        });
                        return null;
                    }
                    if (swapAmount.gt(maxAffordableValue)) {
                        swapAmount = maxAffordableValue;
                        swapTx.value = swapAmount;
                    }
                } catch {
                    // ignore affordability calc if something is missing
                }

                // Store transaction details
                buyTransactions.push({
                    wallet: wallet.address,
                    skipped: false,
                    gasPrice: swapTx.gasPrice ? ethers.utils.formatUnits(swapTx.gasPrice, 'gwei') + ' gwei' : undefined,
                    maxFeePerGas: swapTx.maxFeePerGas ? ethers.utils.formatUnits(swapTx.maxFeePerGas, 'gwei') + ' gwei' : undefined,
                    gasLimit: swapTx.gasLimit ? swapTx.gasLimit.toString() : undefined,
                    swapAmount: ethers.utils.formatEther(swapAmount),
                    balance: ethers.utils.formatEther(balance),
                });

                return wallet.signTransaction(swapTx);
            }));

            // Combine all signed transactions
            const allSignedTxs = [
                signedOpenTradingTx,
                ...signedBuyTxs.filter(Boolean)
            ];

            const relayLabel = getBundleRelayMode();
            console.log(`Sending bundle via ${relayLabel} (API /api/send-bundle)...`);
            console.log('Number of transactions:', allSignedTxs.length);
            
            const bundleDetails = {
                openTrading: {
                    gasPrice: openTradingTx.gasPrice ? ethers.utils.formatUnits(openTradingTx.gasPrice, 'gwei') + ' gwei' : undefined,
                    maxFeePerGas: openTradingTx.maxFeePerGas ? ethers.utils.formatUnits(openTradingTx.maxFeePerGas, 'gwei') + ' gwei' : undefined,
                    gasLimit: openTradingTx.gasLimit ? openTradingTx.gasLimit.toString() : String(this.gasConfig?.topGasLimit || ''),
                },
                buyTransactions: buyTransactions
            };
            
            const result = await sendBundleRelay(allSignedTxs, this.provider, { revertingFromIndex: 1 });
            
            if (result?.error) {
                const err =
                    typeof result.error === 'string'
                        ? result.error
                        : (result.error?.message || JSON.stringify(result.error));
                return { success: false, error: err };
            }

            const expectedTxHashes = allSignedTxs.map((raw) => ethers.utils.keccak256(raw));
            return {
                success: true,
                bundleHash: result?.bundleHash || result?.result?.bundleHash || null,
                rpcUrl: result?.rpcUrl || null,
                expectedTxHashes,
                receipt: {
                    ...result,
                    ...bundleDetails
                },
                walletDetails: walletBalances
            };
        } catch (error) {
            console.error('Enable trading error:', error);
            return { success: false, error: error.message };
        }
    }

    async checkAmount(amount, type = 'max') {
        await this.ensureInitialized();
        const router = new ethers.Contract(this.routerAddress, routerABI, this.masterWallet);
        const path = [this.tokenAddress, this.WCOIN];
        const amountIn = ethers.utils.parseUnits(amount.toString(), this.decimals);

        try {
            const amounts = await router.getAmountsOut(amountIn, path);
            return {
                success: true,
                tokenAmount: ethers.utils.formatUnits(amounts[0], this.decimals),
                ethAmount: ethers.utils.formatEther(amounts[1])
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async approveTokens(wallet) {
        await this.ensureInitialized();
        const userWallet = new ethers.Wallet(wallet.privateKey, this.provider);
        const tokenContract = new ethers.Contract(this.tokenAddress, tokenABI, userWallet);

        try {
            const tx = await tokenContract.approve(
                this.routerAddress,
                ethers.constants.MaxUint256
            );
            const receipt = await tx.wait();
            return { success: true, hash: tx.hash, receipt };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async sellTokens(wallet) {
        await this.ensureInitialized();
        const userWallet = new ethers.Wallet(wallet.privateKey, this.provider);
        const router = new ethers.Contract(this.routerAddress, routerABI, userWallet);
        const tokenContract = new ethers.Contract(this.tokenAddress, tokenABI, userWallet);
        const path = [this.tokenAddress, this.WCOIN];
        const deadline = Math.floor(Date.now() / 1000) + 60 * 20;

        try {
            const tokenBalance = await tokenContract.balanceOf(userWallet.address);
            
            const tx = await router.swapExactTokensForETH(
                tokenBalance,
                0,
                path,
                userWallet.address,
                deadline
            );
            const receipt = await tx.wait();
            return { success: true, hash: tx.hash, receipt };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async sellTokensAuto(params = {}) {
        await this.ensureInitialized();
        const {
            walletPrivateKey,
            walletAddress,
            amountTokens, // string/number in human units
            percent, // 0-100
            phase, // omit = approve+swap; 'approve' | 'swap' for bulk sell-all
        } = params || {};

        const pk = (walletPrivateKey || '').toString().trim();
        if (!pk) return { success: false, error: 'Wallet private key is required' };
        if (!this.tokenAddress || !ethers.utils.isAddress(this.tokenAddress)) return { success: false, error: 'Token address is not set' };
        if (!this.routerAddress || !ethers.utils.isAddress(this.routerAddress)) return { success: false, error: 'Router address is not set' };

        const normalized = pk.startsWith('0x') ? pk : `0x${pk}`;
        const userWallet = new ethers.Wallet(normalized, this.provider);
        if (walletAddress && userWallet.address.toLowerCase() !== String(walletAddress).toLowerCase()) {
            return { success: false, error: 'Private key does not match selected wallet address' };
        }

        const phaseNorm = String(phase || '').toLowerCase();
        const approveOnly = phaseNorm === 'approve';
        const swapOnly = phaseNorm === 'swap';

        const tokenDecimals = getConfig()?.token?.decimals ?? 18;
        const tokenContract = new ethers.Contract(this.tokenAddress, tokenABI, userWallet);
        const router = new ethers.Contract(this.routerAddress, routerABI, userWallet);
        const path = [this.tokenAddress, this.WCOIN];
        const deadline = Math.floor(Date.now() / 1000) + 60 * 20;

        try {
            const feeData = await this.provider.getFeeData().catch(() => null);
            const fees = pickFeeData(feeData, '2');
            const feeOpts = {
                ...(fees?.maxFeePerGas ? { maxFeePerGas: fees.maxFeePerGas, maxPriorityFeePerGas: fees.maxPriorityFeePerGas, type: 2 } : {}),
                ...(fees?.gasPrice ? { gasPrice: fees.gasPrice } : {}),
            };

            const bal = await tokenContract.balanceOf(userWallet.address);

            const computeAmountIn = () => {
                if (bal.isZero()) return null;
                let amountIn = null;
                const p = Number(percent);
                if (Number.isFinite(p) && p > 0) {
                    const pct = Math.min(100, Math.max(0, p));
                    amountIn = bal.mul(Math.floor(pct * 10000)).div(1000000); // 4dp precision
                } else if (amountTokens != null && String(amountTokens).trim() !== '') {
                    amountIn = ethers.utils.parseUnits(String(amountTokens), tokenDecimals);
                } else {
                    return undefined; // invalid input
                }
                if (!amountIn || amountIn.lte(ethers.constants.Zero)) return null;
                if (amountIn.gt(bal)) amountIn = bal;
                return amountIn;
            };

            if (approveOnly) {
                if (bal.isZero()) return { success: true, skipped: true, phase: 'approve' };
                const amountIn = computeAmountIn();
                if (amountIn === undefined) return { success: false, error: 'Enter a percent or token amount' };
                if (!amountIn) return { success: true, skipped: true, phase: 'approve' };
                const allowance = await tokenContract.allowance(userWallet.address, this.routerAddress).catch(() => ethers.constants.Zero);
                if (!allowance.lt(amountIn)) return { success: true, skipped: true, phase: 'approve' };
                const approveTx = await tokenContract.approve(this.routerAddress, ethers.constants.MaxUint256, feeOpts);
                await approveTx.wait(1);
                return { success: true, phase: 'approve', approveHash: approveTx.hash };
            }

            if (swapOnly) {
                if (bal.isZero()) return { success: true, skipped: true, phase: 'swap' };
                const amountIn = computeAmountIn();
                if (amountIn === undefined) return { success: false, error: 'Enter a percent or token amount' };
                if (!amountIn) return { success: true, skipped: true, phase: 'swap' };
                const allowance = await tokenContract.allowance(userWallet.address, this.routerAddress).catch(() => ethers.constants.Zero);
                if (allowance.lt(amountIn)) {
                    return {
                        success: false,
                        error: `Allowance too low for ${userWallet.address.slice(0, 10)}… — run approve phase first`,
                    };
                }
                const swapTx = await router.swapExactTokensForETHSupportingFeeOnTransferTokens(
                    amountIn,
                    0,
                    path,
                    userWallet.address,
                    deadline,
                    feeOpts,
                );
                await swapTx.wait(1);
                return {
                    success: true,
                    phase: 'swap',
                    swapHash: swapTx.hash,
                    soldTokens: ethers.utils.formatUnits(amountIn, tokenDecimals),
                };
            }

            if (bal.isZero()) return { success: false, error: 'Token balance is 0' };
            const amountIn = computeAmountIn();
            if (amountIn === undefined) return { success: false, error: 'Enter a percent or token amount' };
            if (!amountIn) return { success: false, error: 'Sell amount is 0' };

            let approveHash = null;
            const allowance = await tokenContract.allowance(userWallet.address, this.routerAddress).catch(() => ethers.constants.Zero);
            if (allowance.lt(amountIn)) {
                const approveTx = await tokenContract.approve(this.routerAddress, ethers.constants.MaxUint256, feeOpts);
                approveHash = approveTx.hash;
                await approveTx.wait(1);
            }

            const swapTx = await router.swapExactTokensForETHSupportingFeeOnTransferTokens(
                amountIn,
                0,
                path,
                userWallet.address,
                deadline,
                feeOpts,
            );
            await swapTx.wait(1);

            return {
                success: true,
                approveHash,
                swapHash: swapTx.hash,
                soldTokens: ethers.utils.formatUnits(amountIn, tokenDecimals),
            };
        } catch (e) {
            return { success: false, error: e?.message || String(e) };
        }
    }

    async transferTokensAuto(params = {}) {
        await this.ensureInitialized();
        const {
            walletPrivateKey,
            walletAddress,
            to,
            amountTokens, // string/number in human units
            percent, // 0-100
        } = params || {};

        const pk = (walletPrivateKey || '').toString().trim();
        const dst = (to || '').toString().trim();
        if (!pk) return { success: false, error: 'Wallet private key is required' };
        if (!dst || !ethers.utils.isAddress(dst)) return { success: false, error: 'Recipient address is invalid' };
        if (!this.tokenAddress || !ethers.utils.isAddress(this.tokenAddress)) return { success: false, error: 'Token address is not set' };

        const normalized = pk.startsWith('0x') ? pk : `0x${pk}`;
        const userWallet = new ethers.Wallet(normalized, this.provider);
        if (walletAddress && userWallet.address.toLowerCase() !== String(walletAddress).toLowerCase()) {
            return { success: false, error: 'Private key does not match selected wallet address' };
        }

        const tokenDecimals = getConfig()?.token?.decimals ?? 18;
        const tokenContract = new ethers.Contract(this.tokenAddress, tokenABI, userWallet);

        try {
            const bal = await tokenContract.balanceOf(userWallet.address);
            if (bal.isZero()) return { success: false, error: 'Token balance is 0' };

            let amount = null;
            const p = Number(percent);
            if (Number.isFinite(p) && p > 0) {
                const pct = Math.min(100, Math.max(0, p));
                amount = bal.mul(Math.floor(pct * 10000)).div(1000000); // 4dp precision
            } else if (amountTokens != null && String(amountTokens).trim() !== '') {
                amount = ethers.utils.parseUnits(String(amountTokens), tokenDecimals);
            } else {
                return { success: false, error: 'Enter a percent or token amount' };
            }

            if (!amount || amount.lte(ethers.constants.Zero)) return { success: false, error: 'Transfer amount is 0' };
            if (amount.gt(bal)) amount = bal;

            const feeData = await this.provider.getFeeData().catch(() => null);
            const fees = pickFeeData(feeData, '2');

            const tx = await tokenContract.transfer(dst, amount, {
                ...(fees?.maxFeePerGas ? { maxFeePerGas: fees.maxFeePerGas, maxPriorityFeePerGas: fees.maxPriorityFeePerGas, type: 2 } : {}),
                ...(fees?.gasPrice ? { gasPrice: fees.gasPrice } : {}),
            });
            await tx.wait(1);

            return {
                success: true,
                txHash: tx.hash,
                amountTokens: ethers.utils.formatUnits(amount, tokenDecimals),
            };
        } catch (e) {
            return { success: false, error: e?.message || String(e) };
        }
    }

    async removeLiquidity() {
        await this.ensureInitialized();
        const router = new ethers.Contract(this.routerAddress, routerABI, this.masterWallet);
        const tokenContract = new ethers.Contract(this.tokenAddress, tokenABI, this.masterWallet);
        const deadline = Math.floor(Date.now() / 1000) + 60 * 20;

        try {
            const balance = await tokenContract.balanceOf(this.masterWallet.address);
            const tx = await router.removeLiquidityETH(
                this.tokenAddress,
                balance,
                0,
                0,
                this.masterWallet.address,
                deadline
            );
            const receipt = await tx.wait();
            return { success: true, hash: tx.hash, receipt };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async refundToMaster() {
        await this.ensureInitialized();
        /** Balances strictly above this ETH amount are swept (gas + cushion subtracted afterward). */
        const minSweepBalanceWei = ethers.utils.parseEther('0.0002');
        try {
            // Load all wallets
            const wallets = await this.loadWallets();
            const results = [];
            const refundTransactions = [];

            const feeData = await this.provider.getFeeData().catch(() => null);
            const network = await this.provider.getNetwork();
            const gasLimit = ethers.BigNumber.from(21000);
            const use1559 = !!feeData?.maxFeePerGas;

            // Prepare refund transactions for each wallet
            for (const wallet of wallets) {
                try {
                    const walletInstance = new ethers.Wallet(wallet.privateKey, this.provider);
                    const balance = await this.provider.getBalance(wallet.address);

                    if (!balance.gt(minSweepBalanceWei)) {
                        results.push({
                            address: wallet.address,
                            success: false,
                            error: 'Balance ≤ 0.0002 ETH (not eligible for sweep)',
                        });
                        continue;
                    }

                    const gasPrice = use1559
                        ? feeData.maxFeePerGas
                        : feeData?.gasPrice || (await this.provider.getGasPrice());
                    const gasCost = gasLimit.mul(gasPrice);
                    const cushion = ethers.utils.parseEther('0.00005');
                    const refundAmount = balance.sub(gasCost).sub(cushion);
                    if (refundAmount.lte(0)) {
                        results.push({
                            address: wallet.address,
                            success: false,
                            error: 'Balance too low after gas + cushion',
                        });
                        continue;
                    }

                    const nonce = await walletInstance.getTransactionCount('pending');
                    const tx = {
                        to: this.masterWallet.address,
                        value: refundAmount,
                        gasLimit,
                        chainId: network.chainId,
                        nonce,
                        ...(use1559 ? { type: 2 } : { type: 0 }),
                        ...(use1559 ? { maxFeePerGas: feeData.maxFeePerGas } : {}),
                        ...(use1559 && feeData?.maxPriorityFeePerGas
                            ? { maxPriorityFeePerGas: feeData.maxPriorityFeePerGas }
                            : {}),
                        ...(!use1559 ? { gasPrice } : {}),
                    };

                    const signedTx = await walletInstance.signTransaction(tx);
                    refundTransactions.push(signedTx);

                    results.push({
                        address: wallet.address,
                        success: true,
                        amount: ethers.utils.formatEther(refundAmount),
                    });
                } catch (error) {
                    results.push({
                        address: wallet.address,
                        success: false,
                        error: error.message,
                    });
                }
            }

            if (refundTransactions.length > 0) {
                const result = await sendBundleRelay(refundTransactions, this.provider, {
                    blockrazorOnly: true,
                    revertingTxHashes: [],
                });
                if (result.error) {
                    return { success: false, error: result.error.message, results };
                }
                return {
                    success: true,
                    bundleHash: result?.bundleHash || null,
                    hash: result?.bundleHash || result.result,
                    results,
                    numberOfTransactions: refundTransactions.length,
                };
            }

            return { success: true, results };
        } catch (error) {
            console.error('Refund error:', error);
            return { success: false, error: error.message };
        }
    }
}

// Create and export a singleton instance
export const ethereumService = new EthereumService(); 